/**
 * Property Set Manager
 * 
 * Manages IFC property sets and parameter mappings.
 */

// ==================== Types ====================

export type PropertyDataType = 'IfcText' | 'IfcLabel' | 'IfcReal' | 'IfcInteger' | 'IfcBoolean' |
    'IfcLengthMeasure' | 'IfcAreaMeasure' | 'IfcVolumeMeasure' | 'IfcIdentifier';

export interface PropertyTemplate {
    name: string;
    description?: string;
    dataType: PropertyDataType;
    unit?: string;
    defaultValue?: any;
}

export interface PropertySetTemplate {
    id: string;
    name: string;
    applicableCategories: string[];
    properties: PropertyTemplate[];
    isStandard: boolean;
}

export interface PropertyMapping {
    bimParameter: string;
    ifcPropertySet: string;
    ifcProperty: string;
    dataType: PropertyDataType;
    conversionFormula?: string;
}

// ==================== Standard Property Sets ====================

const STANDARD_PSETS: PropertySetTemplate[] = [
    {
        id: 'pset-wall-common',
        name: 'Pset_WallCommon',
        applicableCategories: ['Walls'],
        isStandard: true,
        properties: [
            { name: 'Reference', dataType: 'IfcIdentifier' },
            { name: 'LoadBearing', dataType: 'IfcBoolean' },
            { name: 'IsExternal', dataType: 'IfcBoolean' },
            { name: 'FireRating', dataType: 'IfcLabel' },
            { name: 'AcousticRating', dataType: 'IfcLabel' },
            { name: 'ThermalTransmittance', dataType: 'IfcReal', unit: 'W/(m²·K)' }
        ]
    },
    {
        id: 'pset-slab-common',
        name: 'Pset_SlabCommon',
        applicableCategories: ['Floors', 'Roofs'],
        isStandard: true,
        properties: [
            { name: 'Reference', dataType: 'IfcIdentifier' },
            { name: 'LoadBearing', dataType: 'IfcBoolean' },
            { name: 'IsExternal', dataType: 'IfcBoolean' },
            { name: 'FireRating', dataType: 'IfcLabel' },
            { name: 'PitchAngle', dataType: 'IfcReal', unit: 'deg' }
        ]
    },
    {
        id: 'pset-door-common',
        name: 'Pset_DoorCommon',
        applicableCategories: ['Doors'],
        isStandard: true,
        properties: [
            { name: 'Reference', dataType: 'IfcIdentifier' },
            { name: 'IsExternal', dataType: 'IfcBoolean' },
            { name: 'FireRating', dataType: 'IfcLabel' },
            { name: 'AcousticRating', dataType: 'IfcLabel' },
            { name: 'SecurityRating', dataType: 'IfcLabel' },
            { name: 'HandicapAccessible', dataType: 'IfcBoolean' }
        ]
    },
    {
        id: 'qto-wall',
        name: 'Qto_WallBaseQuantities',
        applicableCategories: ['Walls'],
        isStandard: true,
        properties: [
            { name: 'Length', dataType: 'IfcLengthMeasure', unit: 'm' },
            { name: 'Width', dataType: 'IfcLengthMeasure', unit: 'm' },
            { name: 'Height', dataType: 'IfcLengthMeasure', unit: 'm' },
            { name: 'GrossFootprintArea', dataType: 'IfcAreaMeasure', unit: 'm²' },
            { name: 'NetFootprintArea', dataType: 'IfcAreaMeasure', unit: 'm²' },
            { name: 'GrossSideArea', dataType: 'IfcAreaMeasure', unit: 'm²' },
            { name: 'NetSideArea', dataType: 'IfcAreaMeasure', unit: 'm²' },
            { name: 'GrossVolume', dataType: 'IfcVolumeMeasure', unit: 'm³' },
            { name: 'NetVolume', dataType: 'IfcVolumeMeasure', unit: 'm³' }
        ]
    }
];

// ==================== Property Set Manager ====================

export class PropertySetManager {
    private templates: Map<string, PropertySetTemplate> = new Map();
    private mappings: PropertyMapping[] = [];
    private customPsets: Map<string, PropertySetTemplate> = new Map();

    constructor() {
        for (const pset of STANDARD_PSETS) {
            this.templates.set(pset.id, pset);
        }
        this.createDefaultMappings();
    }

    private createDefaultMappings(): void {
        this.mappings = [
            // Wall mappings
            { bimParameter: 'Type', ifcPropertySet: 'Pset_WallCommon', ifcProperty: 'Reference', dataType: 'IfcIdentifier' },
            { bimParameter: 'Structural', ifcPropertySet: 'Pset_WallCommon', ifcProperty: 'LoadBearing', dataType: 'IfcBoolean' },
            { bimParameter: 'Function', ifcPropertySet: 'Pset_WallCommon', ifcProperty: 'IsExternal', dataType: 'IfcBoolean', conversionFormula: 'value.includes("Exterior")' },
            { bimParameter: 'Fire Rating', ifcPropertySet: 'Pset_WallCommon', ifcProperty: 'FireRating', dataType: 'IfcLabel' },

            // Quantities
            { bimParameter: 'Length', ifcPropertySet: 'Qto_WallBaseQuantities', ifcProperty: 'Length', dataType: 'IfcLengthMeasure' },
            { bimParameter: 'Area', ifcPropertySet: 'Qto_WallBaseQuantities', ifcProperty: 'GrossSideArea', dataType: 'IfcAreaMeasure' },
            { bimParameter: 'Volume', ifcPropertySet: 'Qto_WallBaseQuantities', ifcProperty: 'GrossVolume', dataType: 'IfcVolumeMeasure' },
        ];
    }

    // ==================== Template Operations ====================

    public getTemplate(id: string): PropertySetTemplate | undefined {
        return this.templates.get(id) || this.customPsets.get(id);
    }

    public getTemplateByName(name: string): PropertySetTemplate | undefined {
        for (const t of this.templates.values()) {
            if (t.name === name) return t;
        }
        for (const t of this.customPsets.values()) {
            if (t.name === name) return t;
        }
        return undefined;
    }

    public getTemplatesForCategory(category: string): PropertySetTemplate[] {
        const result: PropertySetTemplate[] = [];
        for (const t of this.templates.values()) {
            if (t.applicableCategories.includes(category)) result.push(t);
        }
        for (const t of this.customPsets.values()) {
            if (t.applicableCategories.includes(category)) result.push(t);
        }
        return result;
    }

    public createCustomPset(
        name: string,
        applicableCategories: string[],
        properties: PropertyTemplate[]
    ): PropertySetTemplate {
        const id = `custom-${Date.now()}`;
        const template: PropertySetTemplate = {
            id,
            name,
            applicableCategories,
            properties,
            isStandard: false
        };
        this.customPsets.set(id, template);
        return template;
    }

    public addPropertyToTemplate(templateId: string, property: PropertyTemplate): void {
        const template = this.customPsets.get(templateId);
        if (template) {
            template.properties.push(property);
        }
    }

    // ==================== Mapping Operations ====================

    public getMappingsForParameter(bimParam: string): PropertyMapping[] {
        return this.mappings.filter(m => m.bimParameter === bimParam);
    }

    public getMappingsForPset(psetName: string): PropertyMapping[] {
        return this.mappings.filter(m => m.ifcPropertySet === psetName);
    }

    public addMapping(mapping: PropertyMapping): void {
        this.mappings.push(mapping);
    }

    public removeMapping(bimParam: string, ifcProperty: string): void {
        this.mappings = this.mappings.filter(
            m => !(m.bimParameter === bimParam && m.ifcProperty === ifcProperty)
        );
    }

    // ==================== Value Conversion ====================

    public convertValue(value: any, mapping: PropertyMapping): any {
        if (mapping.conversionFormula) {
            try {
                return new Function('value', `return ${mapping.conversionFormula}`)(value);
            } catch {
                return value;
            }
        }

        // Type conversions
        switch (mapping.dataType) {
            case 'IfcBoolean':
                return Boolean(value);
            case 'IfcInteger':
                return Math.floor(Number(value) || 0);
            case 'IfcReal':
            case 'IfcLengthMeasure':
            case 'IfcAreaMeasure':
            case 'IfcVolumeMeasure':
                return Number(value) || 0;
            default:
                return String(value);
        }
    }

    // ==================== Export for Element ====================

    public getPropertySetsForElement(element: any): { name: string; properties: Record<string, any> }[] {
        const result: { name: string; properties: Record<string, any> }[] = [];
        const templates = this.getTemplatesForCategory(element.category);

        for (const template of templates) {
            const props: Record<string, any> = {};

            for (const propDef of template.properties) {
                // Find mapping for this property
                const mapping = this.mappings.find(m =>
                    m.ifcPropertySet === template.name && m.ifcProperty === propDef.name
                );

                if (mapping) {
                    const bimValue = element.parameters?.[mapping.bimParameter];
                    if (bimValue !== undefined) {
                        props[propDef.name] = this.convertValue(bimValue, mapping);
                    }
                } else if (propDef.defaultValue !== undefined) {
                    props[propDef.name] = propDef.defaultValue;
                }
            }

            if (Object.keys(props).length > 0) {
                result.push({ name: template.name, properties: props });
            }
        }

        return result;
    }

    // ==================== Statistics ====================

    public getStatistics(): {
        standardPsets: number;
        customPsets: number;
        mappings: number;
    } {
        return {
            standardPsets: Array.from(this.templates.values()).filter(t => t.isStandard).length,
            customPsets: this.customPsets.size,
            mappings: this.mappings.length
        };
    }
}
